import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import './Dashboard.css';
import { dashboardService, vacationService } from '../services/api';

const StatIcon = ({ type }) => {
  const icons = {
    users: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M16 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" />
        <circle cx="9.5" cy="7" r="4" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    projects: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M3 21h18" />
        <path d="M5 21V8l7-5 7 5v13" />
        <path d="M9 21v-7h6v7" />
        <path d="M9 10h.01" />
        <path d="M15 10h.01" />
      </svg>
    ),
    blocked: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="4" y="11" width="16" height="10" rx="2" />
        <path d="M8 11V7a4 4 0 0 1 8 0v4" />
        <path d="m9 17 2 2 4-4" />
      </svg>
    ),
    requests: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="4" y="3" width="16" height="18" rx="2" />
        <path d="M8 7h8" />
        <path d="M8 11h8" />
        <path d="M8 15h5" />
        <path d="M16 17h4" />
      </svg>
    ),
    attendance: (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
        <path d="M7 3 4 6" />
        <path d="M17 3l3 3" />
      </svg>
    )
  };

  return <div className="dashboard-stat-icon">{icons[type]}</div>;
};

const canManageOperations = (role) => ['admin', 'manager', 'supervisor'].includes(role);



const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalProjects: 0,
    pendingRequests: 0,
    todayAttendance: 0,
    blockedUsers: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showVacationModal, setShowVacationModal] = useState(false);
  const [vacationFormData, setVacationFormData] = useState({
    start_date: '',
    end_date: '',
    reason: ''
  });
  const [teamInfo, setTeamInfo] = useState({
    projectName: '',
    teamMembers: []
  });
  const [weeklyPlan, setWeeklyPlan] = useState([]);
  const [actionItems, setActionItems] = useState(null);

  useEffect(() => {
    if (user) {
      loadDashboardData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Safety check for user - only show loading if user is completely missing
  if (!user) {
    return (
      <div className="dashboard-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Načítavam používateľské údaje...</p>
        </div>
      </div>
    );
  }

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      const [summaryData, activityData, statsData, actionData] = await Promise.all([
        dashboardService.getMySummary(),
        dashboardService.getRecentActivity(4),
        user.role === 'admin' ? dashboardService.getStats() : Promise.resolve(null),
        canManageOperations(user.role) ? dashboardService.getActionItems() : Promise.resolve(null)
      ]);

      const weeklyTeam = summaryData.week || {};
      setTeamInfo({
        projectName: weeklyTeam.projectName || weeklyTeam.projects?.map(project => project.name).filter(Boolean).join(', ') || '',
        teamMembers: weeklyTeam.works ? (weeklyTeam.teamMembers || []) : []
      });
      setWeeklyPlan(weeklyTeam.days || []);

      if (user.role === 'admin') {
        setStats({
          totalUsers: statsData.totalUsers || statsData.users?.total || 0,
          totalProjects: statsData.totalProjects || statsData.projects?.total || 0,
          pendingRequests: statsData.pendingVacations || statsData.vacation_requests?.pending || 0,
          todayAttendance: statsData.todayAttendance || statsData.attendance?.today?.users_worked || 0,
          blockedUsers: (statsData.users && typeof statsData.users.blocked === 'number') ? statsData.users.blocked : (statsData.blockedUsers || 0)
        });
      } else {
        setStats({
          totalUsers: 0,
          totalProjects: 0,
          pendingRequests: summaryData.vacation_requests?.pending || 0,
          todayAttendance: summaryData.today?.works ? 1 : 0,
          blockedUsers: 0
        });
      }

      setActionItems(actionData);
      setRecentActivity(activityData);
      setLoading(false);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setTeamInfo({ projectName: '', teamMembers: [] });
      setWeeklyPlan([]);
      setActionItems(null);
      setLoading(false);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Dobré ráno';
    if (hour < 17) return 'Dobrý deň';
    return 'Dobrý večer';
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'attendance': return 'D';
      case 'vacation': return 'V';
      case 'project': return 'P';
      default: return 'I';
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'attendance': return '#28a745';
      case 'vacation': return '#ffc107';
      case 'project': return '#007bff';
      default: return '#6c757d';
    }
  };

  const getDisplayName = (item) => item.full_name || item.username || item.name || 'Bez názvu';

  const getActionItemCards = () => {
    const items = actionItems?.items || {};
    return [
      {
        key: 'pendingVacationRequests',
        title: 'Čakajúce voľno',
        description: 'Žiadosti čakajú na schválenie',
        count: items.pendingVacationRequests?.count || 0,
        sample: items.pendingVacationRequests?.sample || [],
        getPreview: (item) => `${getDisplayName(item)} (${item.start_date} – ${item.end_date})`,
        path: '/vacation-requests',
        severity: 'warning'
      },
      {
        key: 'documentIssues',
        title: 'Dokumenty na riešenie',
        description: 'Chýbajúce, expirované alebo čoskoro expirované dokumenty',
        count: items.documentIssues?.count || 0,
        sample: items.documentIssues?.sample || [],
        getPreview: (item) => `${getDisplayName(item)} - ${item.document_name}`,
        path: '/users',
        severity: 'danger'
      },
      {
        key: 'usersWithoutProject',
        title: 'Používatelia bez projektu',
        description: 'Aktívni používatelia bez priradeného projektu',
        count: items.usersWithoutProject?.count || 0,
        sample: items.usersWithoutProject?.sample || [],
        getPreview: getDisplayName,
        path: '/users',
        severity: 'info'
      },
      {
        key: 'usersWithoutWeekRotation',
        title: 'Bez rotácie tento týždeň',
        description: 'Aktívni používatelia bez plánu v aktuálnom týždni',
        count: items.usersWithoutWeekRotation?.count || 0,
        sample: items.usersWithoutWeekRotation?.sample || [],
        getPreview: (item) => `${getDisplayName(item)}${item.project_name ? ` - ${item.project_name}` : ''}`,
        path: '/projects',
        severity: 'warning'
      },
      {
        key: 'projectsWithoutTeam',
        title: 'Projekty bez tímu',
        description: 'Aktívne projekty bez pracovnej rotácie tento týždeň',
        count: items.projectsWithoutTeam?.count || 0,
        sample: items.projectsWithoutTeam?.sample || [],
        getPreview: (item) => item.location ? `${item.name} - ${item.location}` : item.name,
        path: '/projects',
        severity: 'info'
      },
      {
        key: 'blockedUsers',
        title: 'Blokovaní používatelia',
        description: 'Používatelia označení ako blokovaní',
        count: items.blockedUsers?.count || 0,
        sample: items.blockedUsers?.sample || [],
        getPreview: getDisplayName,
        path: '/users',
        severity: 'neutral'
      }
    ];
  };


  const getWeekStatusLabel = (status) => {
    switch (status) {
      case 'work': return 'Práca';
      case 'vacation': return 'Voľno';
      case 'free': return 'Voľný deň';
      case 'planned': return 'Naplánované';
      default: return 'Bez plánu';
    }
  };

  const formatWeekDate = (date) => new Date(date).toLocaleDateString('sk-SK', {
    day: '2-digit',
    month: '2-digit'
  });

  const actionItemCards = getActionItemCards();
  const totalActionItems = actionItemCards.reduce((total, item) => total + item.count, 0);

  // Quick action handlers
  const handleMarkAttendance = () => {
    navigate('/attendance');
  };

  const handleRequestVacation = () => {
    // Presmerovať na stránku žiadostí s otvoreným modálom
    navigate('/vacation-requests?new=1');
  };

  const handleManageUsers = () => {
    navigate('/users');
  };

  const handleManageProjects = () => {
    navigate('/projects');
  };

  const handleVacationSubmit = async (e) => {
    e.preventDefault();
    try {
      await vacationService.create(vacationFormData);
      setShowVacationModal(false);
      setVacationFormData({ start_date: '', end_date: '', reason: '' });
      // Refresh dashboard data
      loadDashboardData();
      toast.success('Žiadosť o voľno bola úspešne odoslaná.');
    } catch (error) {
      console.error('Error creating vacation request:', error);
      toast.error(error.response?.data?.error || 'Chyba pri vytváraní žiadosti o voľno.');
    }
  };

  const handleVacationInputChange = (e) => {
    const { name, value } = e.target;
    setVacationFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Načítavam dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1 className="dashboard-title">
          {getGreeting()}, {user.full_name || user.username}!
        </h1>
        <p className="dashboard-subtitle">
          Prehľad vášho dochádzového systému
        </p>
        {teamInfo.projectName && teamInfo.teamMembers.length > 0 && (
          <div className="team-info">
            <p className="team-text">
              <strong>Tento týždeň pracuješ s:</strong> {teamInfo.teamMembers.map(m => m.full_name || m.username).join(', ')}
              {' '}na projekte <strong>{teamInfo.projectName}</strong>
            </p>
          </div>
        )}

      </div>

      {weeklyPlan.length > 0 && (
        <section className="my-week-section">
          <div className="my-week-header">
            <div>
              <h2>Môj týždeň</h2>
              <p>Plán práce podľa nastavenej rotácie. Dochádzka sa neoznačuje ručne.</p>
            </div>
          </div>
          <div className="my-week-grid">
            {weeklyPlan.map((day) => (
              <div key={day.date} className={`my-week-day ${day.status}`}>
                <div className="my-week-day-top">
                  <div>
                    <span className="my-week-weekday">{day.weekday}</span>
                    <span className="my-week-date">{formatWeekDate(day.date)}</span>
                  </div>
                  <span className={`my-week-status ${day.status}`}>{getWeekStatusLabel(day.status)}</span>
                </div>
                <div className="my-week-project">
                  {day.projectName || 'Bez projektu'}
                </div>
                {day.projectLocation && (
                  <div className="my-week-location">{day.projectLocation}</div>
                )}
                {day.teamMembers?.length > 0 && (
                  <div className="my-week-team">
                    Tím: {day.teamMembers.map(member => member.full_name || member.username).join(', ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="stats-grid">
        {user.role === 'admin' && (
          <>
            <div className="stat-card users">
              <StatIcon type="users" />
              <div className="stat-content">
                <h3 className="stat-number">{stats.totalUsers}</h3>
                <p className="stat-label">Používateľov</p>
              </div>
            </div>

            <div className="stat-card projects">
              <StatIcon type="projects" />
              <div className="stat-content">
                <h3 className="stat-number">{stats.totalProjects}</h3>
                <p className="stat-label">Projektov</p>
              </div>
            </div>

            <div className="stat-card blocked">
              <StatIcon type="blocked" />
              <div className="stat-content">
                <h3 className="stat-number">{stats.blockedUsers}</h3>
                <p className="stat-label">Blokovaný</p>
              </div>
            </div>
          </>
        )}

        <div className="stat-card requests">
          <StatIcon type="requests" />
          <div className="stat-content">
            <h3 className="stat-number">{stats.pendingRequests}</h3>
            <p className="stat-label">{user.role === 'admin' ? 'Čakajúcich žiadostí' : 'Moje čakajúce žiadosti'}</p>
          </div>
        </div>

        <div className="stat-card attendance">
          <StatIcon type="attendance" />
          <div className="stat-content">
            <h3 className="stat-number">{stats.todayAttendance}</h3>
            <p className="stat-label">{user.role === 'admin' ? 'Dnes pracuje' : 'Dnes pracujem'}</p>
          </div>
        </div>
      </div>

      {canManageOperations(user.role) && actionItems && (
        <section className="admin-action-dashboard">
          <div className="admin-action-header">
            <div>
              <h2>Čo treba riešiť</h2>
              <p>
                Aktuálny týždeň {actionItems.period?.start_date} – {actionItems.period?.end_date}
              </p>
            </div>
            <div className={`admin-action-total ${totalActionItems === 0 ? 'clear' : ''}`}>
              {totalActionItems === 0 ? 'Všetko je v poriadku' : `${totalActionItems} položiek`}
            </div>
          </div>

          {totalActionItems === 0 ? (
            <div className="admin-action-empty">
              Žiadne urgentné úlohy pre admina. Projekty, rotácie, dokumenty a žiadosti sú bez upozornení.
            </div>
          ) : (
            <div className="admin-action-grid">
              {actionItemCards.filter((item) => item.count > 0).map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className={`admin-action-card ${item.severity}`}
                  onClick={() => navigate(item.path)}
                >
                  <div className="admin-action-card-main">
                    <span className="admin-action-count">{item.count}</span>
                    <div>
                      <h3>{item.title}</h3>
                      <p>{item.description}</p>
                    </div>
                  </div>
                  {item.sample.length > 0 && (
                    <ul className="admin-action-preview">
                      {item.sample.slice(0, 3).map((sampleItem, index) => (
                        <li key={`${item.key}-${index}`}>{item.getPreview(sampleItem)}</li>
                      ))}
                    </ul>
                  )}
                </button>
              ))}
            </div>
          )}
        </section>
      )}


      <div className="dashboard-content">
        <div className="activity-section">
          <h2 className="section-title">Posledná aktivita</h2>
          <div className="activity-list">
            {recentActivity.map(activity => (
              <div key={activity.id} className="activity-item">
                <div
                  className="activity-icon"
                  style={{ backgroundColor: getActivityColor(activity.type) }}
                >
                  {getActivityIcon(activity.type)}
                </div>
                <div className="activity-content">
                  <div className="activity-main">
                    <strong>{activity.user}</strong> {activity.action}
                    {activity.location && <span className="activity-detail"> - {activity.location}</span>}
                    {activity.period && <span className="activity-detail"> ({activity.period})</span>}
                    {activity.project && <span className="activity-detail"> - {activity.project}</span>}
                  </div>
                  <div className="activity-time">
                    {activity.date} o {activity.time}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="quick-actions">
          <h2 className="section-title">Rýchle akcie</h2>
          <div className="action-buttons">
            <button className="action-btn attendance-btn" onClick={handleMarkAttendance}>
              Označiť dochádzku
            </button>
            <button className="action-btn vacation-btn" onClick={handleRequestVacation}>
              Požiadať o voľno
            </button>
            {user.role === 'admin' && (
              <>
                <button className="action-btn users-btn" onClick={handleManageUsers}>
                  Spravovať používateľov
                </button>
                <button className="action-btn projects-btn" onClick={handleManageProjects}>
                  Spravovať projekty
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="dashboard-footer">
        <div className="system-info">
          <p>
            <strong>ConstructAP s.r.o.</strong> - Dochádzový systém v 2.9.11
          </p>
          <p>
            Posledná aktualizácia: {new Date().toLocaleDateString('sk-SK', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit'
            })}
          </p>
        </div>
      </div>

      {/* Attendance Modal - Removed old check-in/out/break system */}

      {/* Vacation Request Modal */}
      {showVacationModal && (
        <div className="modal-overlay" onClick={() => setShowVacationModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Požiadať o voľno</h3>
              <button className="close-btn" onClick={() => setShowVacationModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleVacationSubmit}>
                <div className="form-group">
                  <label htmlFor="start_date">Dátum od:</label>
                  <input
                    type="date"
                    id="start_date"
                    name="start_date"
                    value={vacationFormData.start_date}
                    onChange={handleVacationInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="end_date">Dátum do:</label>
                  <input
                    type="date"
                    id="end_date"
                    name="end_date"
                    value={vacationFormData.end_date}
                    onChange={handleVacationInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="reason">Dôvod:</label>
                  <textarea
                    id="reason"
                    name="reason"
                    value={vacationFormData.reason}
                    onChange={handleVacationInputChange}
                    placeholder="Zadajte dôvod žiadosti o voľno..."
                    rows="3"
                    required
                  />
                </div>
                <div className="form-actions">
                  <button type="button" className="cancel-btn" onClick={() => setShowVacationModal(false)}>
                    Zrušiť
                  </button>
                  <button type="submit" className="submit-btn">
                    Odoslať žiadosť
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
